import { useContext } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import classnames from 'classnames';

import * as tracking from 'dibs-tracking';
import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';
import ItemHeartWrapper from 'dibs-portfolio-heart/exports/ItemHeartWrapper';
import ItemFolderWrapper from 'dibs-portfolio-folder/exports/ItemFolderWrapper';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';

import { ServerVarsContext } from '../../../sharedComponents/ServerVarsContext';
import folderTracking from '../../helpers/storefrontFolderTracking';
import { storefrontEnsureLogin } from '../../mutations/storefrontEnsureLogin';

import styles from './styles/StorefrontProductTile.scss';

function fireProductFavoriting(action, itemId) {
    let trackObj = {
        action,
        label: 'search/browse',
        category: 'item favoriting',
        isInteractiveEvent: true,
    };

    if (itemId) {
        trackObj = {
            ...trackObj,
            additional: {
                itemId,
            },
        };
    }

    tracking.trackEvent(trackObj, null);
}
const StorefrontProductTile = ({
    relay,
    index,
    userId,
    userIds,
    viewer,
    product,
    activeQuickView,
    openQuickView,
    closeQuickView,
    currency,
    showCurrency,
}) => {
    const { isMobile } = useContext(ServerVarsContext);
    const { title, photos, serviceId: itemId } = product || {};
    const image = photos?.[0]?.versions?.[0]?.webPath;

    const getFavoritesComponent = ({
        isFolderVisible,
        onFavorited,
        onUnFavorited,
        onFolderActionStart,
        onFolderActionEnd,
    }) => {
        const sharedIconClasses = classnames(styles.icon, {
            [styles.enlargedIcon]: !isMobile,
        });

        return (
            <div className={styles.favoritesWrapper}>
                {/* Heart */}
                <div
                    className={classnames(sharedIconClasses, {
                        [styles.favoritesSpacing]: !isMobile,
                    })}
                >
                    <ItemHeartWrapper
                        authModalShow={cb =>
                            storefrontEnsureLogin({
                                relayEnvironment: relay.environment,
                                authOptions: {
                                    ga: {
                                        label: 'save item - dealer storefront page',
                                    },
                                },
                            })
                                .then(cb)
                                .catch(() => {})
                        }
                        item={product}
                        loadPortfolioData={!!userId}
                        onFavorited={() => {
                            fireProductFavoriting('item added', itemId);
                            onFavorited?.();
                        }}
                        onUnFavorited={() => {
                            fireProductFavoriting('item removed', itemId);
                            onUnFavorited?.();
                        }}
                        viewer={viewer}
                        userId={userId}
                        userIds={userIds}
                        darkTheme
                    />
                </div>
                {/* Folder */}
                {!isMobile && isFolderVisible && (
                    <div className={sharedIconClasses}>
                        <ItemFolderWrapper
                            authModalTrackingLabel="save item - search/browse"
                            item={product}
                            selectedItemImages={image ? [image] : []}
                            selectedItemTitles={title ? [title] : []}
                            shouldFillInFolder={!!userId}
                            singlePortfolioItem
                            trackingHooks={folderTracking}
                            getModalComponent={() => {
                                return storefrontEnsureLogin({
                                    relayEnvironment: relay.environment,
                                    authOptions: {
                                        shouldPauseAuthFlow: state => state.step === 0,
                                    },
                                })
                                    .then(() => {
                                        return import(
                                            /* webpackChunkName: "AddToFolderModalWrapper" */
                                            'dibs-portfolio-folder/src/components/AddToFolderModalWrapper'
                                        ).then(exported => exported.default);
                                    })
                                    .catch(() => {});
                            }}
                            onAddItemSuccess={() => {
                                if (authModalLoader.isPaused()) {
                                    authModalLoader.resume();
                                }

                                onFolderActionEnd?.();
                            }}
                            userId={userId}
                            userIds={userIds}
                            viewer={viewer}
                            darkTheme
                            showPlusIcon
                            onFolderSave={onFolderActionStart}
                            onFolderRemove={onFolderActionStart}
                            onNewFolderSave={onFolderActionStart}
                            onRemoveItemSuccess={onFolderActionEnd}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <FavoritesItemProvider item={product} applyMouseEvents={!isMobile}>
            {favoritesItemProps => (
                <ItemTile
                    index={index}
                    isMobile={isMobile}
                    item={product}
                    itemSearch={null}
                    itemsPerRow={4}
                    currency={currency}
                    showPrice={!!currency && showCurrency}
                    showSellerName={false}
                    onClick={() => {
                        tracking.trackEcommerce({
                            type: 'productClick',
                            actionField: {
                                list: 'storefront featured listing',
                                itemId,
                            },
                            products: [product.ecommerceTrackingParams],
                        });
                    }}
                    showQuickView
                    activeQuickView={activeQuickView}
                    closeQuickView={closeQuickView}
                    openQuickView={openQuickView}
                    useLoFiLazyLoader
                    viewer={null}
                    favoritesComponent={getFavoritesComponent(favoritesItemProps)}
                    showMSKUSwatch={false}
                    showMeasurements={false}
                    imageSize={isMobile ? 'imageSmall' : 'imageMedium'}
                    hideImageSidePadding={isMobile}
                />
            )}
        </FavoritesItemProvider>
    );
};

StorefrontProductTile.propTypes = {
    openQuickView: PropTypes.func.isRequired,
    closeQuickView: PropTypes.func.isRequired,
    activeQuickView: PropTypes.number,

    relay: PropTypes.object.isRequired,

    // relay variabales
    userId: PropTypes.string,
    userIds: PropTypes.array,
    isTrade: PropTypes.bool,
    currency: PropTypes.string,
    showCurrency: PropTypes.bool,
    index: PropTypes.number.isRequired,

    // relay fragments
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    product: PropTypes.object.isRequired,
};

export default createFragmentContainer(StorefrontProductTile, {
    viewer: graphql`
        fragment StorefrontProductTile_viewer on Viewer {
            ...ItemHeartWrapper_viewer @arguments(loadPortfolioData: $hasUserId, userIds: $userIds)
        }
    `,
    user: graphql`
        fragment StorefrontProductTile_user on User {
            serviceId
        }
    `,
    product: graphql`
        fragment StorefrontProductTile_product on Item {
            ...ItemFolderWrapper_item @arguments(userId: $userId, loadPortfolioData: $hasUserId)
            ...ItemHeartWrapper_item @arguments(userId: $userId, loadPortfolioData: $hasUserId)
            ...ItemTile_item @arguments(isTrade: $isTrade)
            ...FavoritesItemProvider_item
            serviceId
            title
            photos {
                versions {
                    webPath
                }
            }
            ecommerceTrackingParams
        }
    `,
});

export { StorefrontProductTile as Raw };
